import React, { useState } from 'react';
import { TextInput } from 'react-admin';
import { getCodeInputStyle } from '../utils/codeStyles';
import { isValidCode } from '../utils/codeValidators';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { InputAdornment, Tooltip } from '@mui/material';

/**
 * A specialized input component for code fields with validation visual feedback
 * Features:
 * - Green border when valid
 * - Green check icon when valid
 * - Consistent styling across the app
 * - Skip validation in edit mode to allow legacy code names
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.editMode - If true, skips validation (for editing existing codes)
 * @returns {JSX.Element} Rendered component
 */
const CodeInput = (props) => {
    const { editMode = false, ...restProps } = props;
    const [isValid, setIsValid] = useState(editMode ? true : false); // Default to valid in edit mode
    
    const handleChange = (event) => {
        const value = event.target.value;
        
        // Skip validation in edit mode, otherwise validate normally
        if (editMode) {
            setIsValid(true);
        } else {
            setIsValid(isValidCode(value));
        }
        
        // Call the original onChange handler if provided
        if (props.onChange) {
            props.onChange(event);
        }
    };
    
    return (
        <TextInput
            {...restProps}
            onChange={handleChange}
            sx={getCodeInputStyle(isValid, editMode)}
            fullWidth
            InputProps={{
                ...restProps.InputProps,
                endAdornment: (isValid && !editMode) ? (
                    <InputAdornment position="end">
                        <Tooltip title="Valid code format">
                            <CheckCircleOutlineIcon color="success" />
                        </Tooltip>
                    </InputAdornment>
                ) : undefined,
            }}
        />
    );
};

export default CodeInput; 